import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const brandHighlights = [
  {
    icon: 'ri-shield-star-line',
    title: '权威可靠',
    description: '所有 caoliu 入口地址均经过官方验证，确保用户访问的是真实安全的官方站点，杜绝钓鱼风险。',
  },
  {
    icon: 'ri-time-line',
    title: '实时更新',
    description: '我们密切跟踪 caoliu 官方动态，第一时间同步最新入口地址、功能变化和使用指南。',
  },
  {
    icon: 'ri-book-read-line',
    title: '内容全面',
    description: '涵盖官方入口、使用教程、安全访问、社区指南、镜像站点等全方面的 caoliu 使用内容。',
  },
  {
    icon: 'ri-user-heart-line',
    title: '用户至上',
    description: '以用户需求为中心，持续优化内容质量和阅读体验，让每位 caoliu 用户都能找到所需。',
  },
];

const stats = [
  { value: '50+', label: '原创文章' },
  { value: '6', label: '内容分类' },
  { value: '120K+', label: '月访问量' },
  { value: `${new Date().getFullYear() - 2018}+`, label: '运营年数' },
];

const contactMethods = [
  {
    icon: 'ri-telegram-line',
    title: 'Telegram 频道',
    description: '关注我们的 Telegram 频道，获取 caoliu 最新动态和入口更新通知',
    href: siteInfo.socialLinks.telegram,
    isExternal: true,
  },
  {
    icon: 'ri-twitter-x-line',
    title: 'Twitter / X',
    description: '通过 Twitter 关注我们，获取 caoliu 相关的实时资讯和社区动态',
    href: siteInfo.socialLinks.twitter,
    isExternal: true,
  },
  {
    icon: 'ri-mail-line',
    title: '电子邮件',
    description: `发送邮件至 ${siteInfo.contactEmail}，我们将在24小时内回复`,
    href: siteInfo.socialLinks.email,
    isExternal: true,
  },
];

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: siteInfo.name,
  url: 'https://caoliu-guide.com',
  description: siteInfo.description,
  foundingDate: siteInfo.foundingYear,
  contactPoint: {
    '@type': 'ContactPoint',
    email: siteInfo.contactEmail,
    contactType: 'customer service',
  },
  sameAs: [siteInfo.socialLinks.telegram, siteInfo.socialLinks.twitter],
};

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: `关于我们 - ${siteInfo.name}`,
  description: siteInfo.description,
  url: 'https://caoliu-guide.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: siteInfo.name,
    url: 'https://caoliu-guide.com',
  },
  about: {
    '@type': 'Organization',
    name: siteInfo.name,
  },
};

export default function AboutPage() {
  return (
    <Layout>
      <SEOHead
        title={`关于我们 - ${siteInfo.name}`}
        description={`了解${siteInfo.name}的品牌故事、核心优势与联系方式。${siteInfo.description}`}
        keywords="caoliu导航,关于caoliu,caoliu联系方式,caoliu品牌介绍"
        canonicalUrl="https://caoliu-guide.com/about"
        schemaJsonLd={[orgSchema, aboutSchema]}
      />

      <div className="pt-24 pb-10 px-4 md:px-6 bg-background-100/60">
        <div className="max-w-7xl mx-auto">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
          <div className="max-w-3xl">
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
              关于 {siteInfo.name}
            </h1>
            <p className="text-foreground-600 leading-relaxed text-base">
              {siteInfo.description}
            </p>
          </div>
        </div>
      </div>

      <section className="py-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
            品牌介绍
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="text-lg font-semibold text-foreground-950 mb-4">
                我们的使命
              </h3>
              <p className="text-foreground-600 leading-relaxed mb-4">
                {siteInfo.name} 成立于 {siteInfo.foundingYear} 年，是中文互联网上最全面、最权威的 caoliu 社区导航平台。我们致力于为 caoliu 用户提供一站式的入口导航、功能介绍和使用指导服务，帮助每一位用户安全、便捷地访问和使用 caoliu 社区的丰富资源。
              </p>
              <p className="text-foreground-600 leading-relaxed mb-4">
                在网络环境日益复杂的今天，找到可靠的 caoliu 官方入口变得越来越困难。我们的团队由资深 caoliu 用户和技术爱好者组成，凭借多年的社区经验和对网络技术的深入理解，持续追踪和验证 caoliu 最新官方地址，为用户提供最准确、最及时的入口信息。
              </p>
              <p className="text-foreground-600 leading-relaxed">
                除了入口导航，我们还精心编写了大量高质量的 caoliu 使用教程和指南文章。从新手入门到高级技巧，从账号安全到社区规则，我们的目标是让每一位用户都能在 caoliu 社区中获得最佳的使用体验。
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden bg-background-100">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20office%20team%20collaboration%20concept%20with%20abstract%20digital%20elements%2C%20soft%20warm%20lighting%2C%20minimalist%20workspace%20illustration%2C%20creative%20team%20atmosphere%2C%20editorial%20style%2C%20professional%20yet%20friendly%20visual%2C%20clean%20composition&width=800&height=600&seq=about-mission&orientation=landscape"
                alt={`${siteInfo.name} 团队理念`}
                className="w-full h-full object-cover"
                width={800}
                height={600}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 md:px-6 bg-background-100/60">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3 text-center">
            为什么选择我们
          </h2>
          <p className="text-foreground-600 text-center mb-12 max-w-xl mx-auto">
            作为最受信赖的 caoliu 导航平台，我们拥有以下核心优势
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {brandHighlights.map((item) => (
              <div
                key={item.title}
                className="bg-background-50 rounded-xl border border-background-200/60 p-6 text-center"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-4">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-10 text-center">
            平台数据
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl mx-auto">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-primary-500">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-500 mt-2">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 md:px-6 bg-background-100/60">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3 text-center">
            我们的内容
          </h2>
          <p className="text-foreground-600 text-center mb-12 max-w-xl mx-auto">
            覆盖 caoliu 使用全场景的内容体系，总有一篇适合您
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => (
              <div
                key={cat.slug}
                className="bg-background-50 rounded-xl border border-background-200/60 p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={cat.icon}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950">
                    {cat.name}
                  </h3>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {cat.description}
                </p>
                <p className="text-xs text-foreground-500 mt-3">
                  {cat.articleCount} 篇文章
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-3 text-center">
            联系我们
          </h2>
          <p className="text-foreground-600 text-center mb-12 max-w-xl mx-auto">
            如有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            {contactMethods.map((method) => (
              <a
                key={method.title}
                href={method.href}
                target={method.isExternal ? '_blank' : undefined}
                rel={method.isExternal ? 'nofollow noopener noreferrer' : undefined}
                className="bg-background-50 rounded-xl border border-background-200/60 p-6 text-center hover:border-primary-200/60 transition-all group block"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mx-auto mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-all">
                  <i className={`${method.icon} text-xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">
                  {method.title}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {method.description}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}